import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserStore } from "../stores/userStore";
import { appConfig } from "../config/appConfig";

const IntercomContext = createContext();

export const fetchIntercomUserHash = async (email) => {
  const { url, version } = appConfig.api;
  try {
    const response = await fetch(
      `${url.replace(/\/$/, "")}/${version}/system/integrations/intercom/hash`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_email: email }),
      },
    );
    const { error, result } = await response.json();
    return error ? null : (result?.intercom_hash ?? null);
  } catch {
    return null;
  }
};

/** Domains that should not be identified to Intercom (Ordering+ parity + Finitless internal). */
// const shouldSkipIntercomIdentify = (email) => {
//   if (!email || typeof email !== "string") {
//     return true;
//   }
//   const lower = email.toLowerCase();
//   return (
//     lower.includes("@ordering.co") || lower.includes("@finitless.com")
//   );
// };

/**
 * IntercomProvider component
 * Provides Intercom functionality throughout the app
 * Automatically initializes Intercom and updates user data when auth changes
 */
export const IntercomProvider = ({ children, useIntercom }) => {
  const { isAuthenticated } = useAuth();
  const user = useUserStore((state) => state.user);

  // Use provided hook or create default
  const defaultIntercom = () => ({
    isEnabled: false,
    isIntercomBooted: true,
    initialize: () => {},
    updateUser: () => {},
    trackEvent: () => {},
    hideMessenger: () => {},
    shutdown: () => {},
    boot: () => {},
  });
  const useIntercomHook = useIntercom || defaultIntercom;
  const intercom = useIntercomHook();

  const activeIntercomEmail = useRef(null);
  const [identity, setIdentity] = useState({ email: null, hash: null });
  const identityEmail = isAuthenticated ? user?.email?.trim() : null;

  useEffect(() => {
    if (!intercom.isEnabled || !identityEmail) {
      return undefined;
    }
    let cancelled = false;
    fetchIntercomUserHash(identityEmail).then((hash) => {
      if (!cancelled) {
        setIdentity({ email: identityEmail, hash });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [intercom.isEnabled, identityEmail]);

  useEffect(() => {
    if (!intercom.isEnabled) {
      return;
    }

    if (isAuthenticated && user) {
      const email = user.email?.trim();
      if (!email || identity.email !== email) {
        return;
      }

      // if (shouldSkipIntercomIdentify(email)) {
      //   intercom.hideMessenger();
      //   return;
      // }

      const displayName =
        user.name || `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();

      const userData = {
        user_id: email,
        email,
        user_hash: identity.hash || undefined,
        name: displayName || undefined,
        created_at: user.created_at,
        custom_attributes: {
          user_level: user.level,
          business_id: user.business_id,
          dashboard_user_id: user.id,
        },
      };

      if (
        intercom.isIntercomBooted &&
        activeIntercomEmail.current &&
        activeIntercomEmail.current !== email
      ) {
        intercom.shutdown();
        activeIntercomEmail.current = email;
        setTimeout(() => {
          intercom.boot(userData);
        }, 200);
      } else if (
        !intercom.isIntercomBooted ||
        activeIntercomEmail.current !== email
      ) {
        intercom.boot(userData);
        activeIntercomEmail.current = email;
      } else {
        intercom.updateUser(userData);
      }

      intercom.trackEvent("user_logged_in", {
        user_id: user.id,
        timestamp: new Date().toISOString(),
      });
    } else if (!isAuthenticated && intercom.isEnabled) {
      intercom.trackEvent("user_logged_out", {
        timestamp: new Date().toISOString(),
      });

      intercom.hideMessenger();
      intercom.shutdown();
      activeIntercomEmail.current = null;
    }
  }, [isAuthenticated, user, intercom, intercom.isIntercomBooted, identity]);
  const value = {
    ...intercom,
  };

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};

/**
 * Hook to use Intercom context
 * @returns {Object} Intercom methods and state
 */
export const useIntercomContext = () => {
  const context = useContext(IntercomContext);
  if (!context) {
    throw new Error(
      "useIntercomContext must be used within an IntercomProvider",
    );
  }
  return context;
};

export default IntercomContext;
